import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { MaleFormData } from '@/utils/maleCalculator';
import { trackEvent } from '@/lib/analytics';

interface MaleCalculatorFormProps {
  onCalculate: (data: MaleFormData) => void;
  /** Prefill from a shared link. */
  initialData?: MaleFormData;
}

const defaultFormData: MaleFormData = {
  ageRange: [25, 40],
  race: ['all'],
  heightMin: 'no-preference',
  incomeMin: 'no-preference',
  politics: 'no-preference',
  maritalStatus: 'no-preference',
  bodyType: 'no-preference',
  educationalLevel: 'no-preference',
  employment: 'no-preference',
};

export function MaleCalculatorForm({ onCalculate, initialData }: MaleCalculatorFormProps) {
  const [formData, setFormData] = useState<MaleFormData>(initialData ?? defaultFormData);

  const updateField = <K extends keyof MaleFormData>(field: K, value: MaleFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    trackEvent('factor_changed', { factor: field, calculator: 'men' });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCalculate(formData);
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>Male Partner Calculator</CardTitle>
        <CardDescription>
          Enter your preferences to calculate the share of US men who match, based on Census, CDC, and BLS data
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Age Range */}
          <div className="space-y-3">
            <Label>Age Range: {formData.ageRange[0]} - {formData.ageRange[1]} years</Label>
            <Slider
              value={formData.ageRange}
              onValueChange={(value) => updateField('ageRange', value as [number, number])}
              min={18}
              max={100}
              step={1}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Based on U.S. Census Bureau American Community Survey data
            </p>
          </div>

          {/* Race/Ethnicity */}
          <div className="space-y-3">
            <Label>Race/Ethnicity Preference (Select all that apply)</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { value: 'all', label: 'All (No Preference)' },
                { value: 'white', label: 'White (Non-Hispanic)' },
                { value: 'black', label: 'Black or African American' },
                { value: 'hispanic', label: 'Hispanic or Latino' },
                { value: 'asian', label: 'Asian' },
                { value: 'native-american', label: 'Native American or Alaska Native' },
                { value: 'middle-eastern', label: 'Middle Eastern or North African' },
                { value: 'pacific-islander', label: 'Pacific Islander' },
                { value: 'multiracial', label: 'Multiracial/Mixed Race' },
                { value: 'other', label: 'Other' }
              ].map((option) => (
                <div key={option.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={`male-${option.value}`}
                    checked={formData.race.includes(option.value)}
                    onCheckedChange={(checked) => {
                      if (option.value === 'all') {
                        updateField('race', checked ? ['all'] : []);
                      } else {
                        let newRace = formData.race.filter(r => r !== 'all');
                        if (checked) {
                          newRace = [...newRace, option.value];
                        } else {
                          newRace = newRace.filter(r => r !== option.value);
                        }
                        if (newRace.length === 0) {
                          newRace = ['all'];
                        }
                        updateField('race', newRace);
                      }
                    }}
                  />
                  <Label htmlFor={`male-${option.value}`} className="text-sm font-normal cursor-pointer">
                    {option.label}
                  </Label>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Based on U.S. Census Bureau American Community Survey data (some categories use estimated data)
            </p>
          </div>

          {/* Minimum Height */}
          <div className="space-y-2">
            <Label>Minimum Height</Label>
            <Select value={formData.heightMin} onValueChange={(value) => updateField('heightMin', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-preference">No Preference</SelectItem>
                <SelectItem value="5-8">5'8" or taller</SelectItem>
                <SelectItem value="5-10">5'10" or taller</SelectItem>
                <SelectItem value="6-0">6'0" or taller</SelectItem>
                <SelectItem value="6-2">6'2" or taller</SelectItem>
                <SelectItem value="6-4">6'4" or taller</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Based on CDC NHANES anthropometric reference data (average US man is about 5'9")
            </p>
          </div>

          {/* Minimum Income */}
          <div className="space-y-2">
            <Label>Minimum Annual Income</Label>
            <Select value={formData.incomeMin} onValueChange={(value) => updateField('incomeMin', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-preference">No Preference</SelectItem>
                <SelectItem value="50k">$50,000+</SelectItem>
                <SelectItem value="75k">$75,000+</SelectItem>
                <SelectItem value="100k">$100,000+</SelectItem>
                <SelectItem value="200k">$200,000+</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Based on U.S. Census Bureau and BLS individual income data
            </p>
          </div>

          {/* Marital Status */}
          <div className="space-y-2">
            <Label>Marital Status Preference</Label>
            <Select value={formData.maritalStatus} onValueChange={(value) => updateField('maritalStatus', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-preference">No Preference</SelectItem>
                <SelectItem value="never-married">Never Married</SelectItem>
                <SelectItem value="divorced-separated">Divorced/Separated</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Based on U.S. Census Bureau Marital Status data
            </p>
          </div>

          {/* Political Leaning */}
          <div className="space-y-2">
            <Label>Political Leaning Preference</Label>
            <Select value={formData.politics} onValueChange={(value) => updateField('politics', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-preference">No Preference</SelectItem>
                <SelectItem value="conservative">Conservative</SelectItem>
                <SelectItem value="moderate">Moderate</SelectItem>
                <SelectItem value="liberal">Liberal</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Based on Pew Research Center Political Typology data
            </p>
          </div>

          {/* Body Type */}
          <div className="space-y-2">
            <Label>Body Type Preference</Label>
            <Select value={formData.bodyType} onValueChange={(value) => updateField('bodyType', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-preference">No Preference</SelectItem>
                <SelectItem value="not-obese">Not Obese (BMI &lt; 30)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Based on CDC National Health and Nutrition Examination Survey data
            </p>
          </div>

          {/* Educational Level */}
          <div className="space-y-2">
            <Label>Educational Level Preference</Label>
            <Select value={formData.educationalLevel} onValueChange={(value) => updateField('educationalLevel', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-preference">No Preference</SelectItem>
                <SelectItem value="high_school_or_less">High School or Less</SelectItem>
                <SelectItem value="some_college">Some College</SelectItem>
                <SelectItem value="bachelors_degree">Bachelor's Degree</SelectItem>
                <SelectItem value="graduate_degree">Graduate Degree</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Based on U.S. Census Bureau Educational Attainment data
            </p>
          </div>

          {/* Employment */}
          <div className="space-y-2">
            <Label>Employment Status</Label>
            <Select value={formData.employment} onValueChange={(value) => updateField('employment', value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select preference" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-preference">No Preference</SelectItem>
                <SelectItem value="employed">Employed</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Based on Bureau of Labor Statistics employment data
            </p>
          </div>

          <Button type="submit" className="w-full" size="lg">
            Calculate Compatibility Probability
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
